import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Filter, Search, Map, List, Grid, ChevronDown, MapPin } from 'lucide-react';
import Button from '../components/ui/Button';
import ToolCard from '../components/features/ToolCard';
import MapView from '../components/features/MapView';
import { Tool } from '../types';
import { useQuery } from '@apollo/client';
import { GET_BROWSE_TOOLS_SAFE } from '../graphql/queries/browseToolsSafe';
import { GET_CATEGORIES } from '../graphql/queries/categories';
import { Category } from '../types/Category';

const MIN_DISTANCE_M = 20;
const MAX_DISTANCE_M = 5000;
const DEFAULT_DISTANCE_M = 100;
const BROWSE_STATE_STORAGE_KEY = 'browse-tools-state-v1';

interface BrowseFilters {
  categories: string[];
  priceRange: [number, number];
  availability: string[];
  rating: number;
  distance: number;
  cities: string;
  postalCodes: string;
  useDistanceFilter: boolean;
}

interface StoredBrowseState {
  query?: string;
  viewMode?: 'grid' | 'list' | 'map';
  sortBy?: string;
  currentPage?: number;
  tempDistance?: number;
  tempCities?: string;
  tempPostalCodes?: string;
  filters?: Partial<BrowseFilters>;
}

const BrowseTools: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [tools, setTools] = useState<Tool[]>([]);
  const [filteredTools, setFilteredTools] = useState<Tool[]>([]);
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'map'>('grid');
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [userLocation, setUserLocation] = useState<{latitude: number; longitude: number} | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [tempDistance, setTempDistance] = useState(DEFAULT_DISTANCE_M);
  const [tempCities, setTempCities] = useState('');
  const [tempPostalCodes, setTempPostalCodes] = useState('');
  const [sortBy, setSortBy] = useState<string>('relevance');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 9;
  const [filters, setFilters] = useState<BrowseFilters>({
    categories: searchParams.get('category') ? [searchParams.get('category') as string] : [],
    priceRange: [0, 500],
    availability: [],
    rating: 0,
    distance: DEFAULT_DISTANCE_M,
    cities: '',
    postalCodes: '',
    useDistanceFilter: true,
  });
  const { data: catData } = useQuery(GET_CATEGORIES);
  const [categories, setCategories] = useState<Category[]>([]); 

  const { data: browseData } = useQuery(GET_BROWSE_TOOLS_SAFE, {
    variables: {
      limit: 500,
      offset: 0,
      date: new Date().toISOString().split('T')[0]
    },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (catData) {
      setCategories(catData.categories);
    }
  }, [catData])

  // Parse category_ids from URL on mount
  useEffect(() => {
    const categoryIdsParam = searchParams.get('category_ids');
    if (categoryIdsParam && categories.length > 0) {
      const categoryIds = categoryIdsParam.split(',').filter(Boolean);
      const categoryNames = categoryIds
        .map(id => categories.find(c => c.id === id)?.name)
        .filter(Boolean) as string[];
      
      if (categoryNames.length > 0) {
        setFilters(prev => ({
          ...prev,
          categories: categoryNames
        }));
      }
    }
  }, [categories, searchParams]);

  useEffect(() => {
    // Hent brugerens position
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          setLocationError(null);
        },
        (error) => {
          console.error('Geolocation error:', error);
          setLocationError('Kunne ikke hente din position');
        }
      );
    } else {
      setLocationError('Geolocation understøttes ikke af din browser');
    }
  }, []);

  const calculateDistanceKm = (lat1: number, lng1: number, lat2: number, lng2: number) => {
    const toRad = (value: number) => (value * Math.PI) / 180;
    const earthRadiusKm = 6371;
    const deltaLat = toRad(lat2 - lat1);
    const deltaLng = toRad(lng2 - lng1);
    const a =
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadiusKm * c;
  };

  const getToolCoordinates = (tool: Tool) => {
    if (
      typeof tool.locationArea?.latitude === 'number' &&
      typeof tool.locationArea?.longitude === 'number'
    ) {
      return {
        latitude: tool.locationArea.latitude,
        longitude: tool.locationArea.longitude,
      };
    }

    if (typeof tool.latitude === 'number' && typeof tool.longitude === 'number') {
      return {
        latitude: tool.latitude,
        longitude: tool.longitude,
      };
    }

    return null;
  };

  const clampDistance = (distance: number) => {
    if (!Number.isFinite(distance)) return DEFAULT_DISTANCE_M;
    return Math.min(MAX_DISTANCE_M, Math.max(MIN_DISTANCE_M, Math.round(distance)));
  };

  const buildToolDetailLink = (toolId: string) => {
    const returnTo = `${location.pathname}${location.search}` || '/browse';
    return `/tool/${toolId}?returnTo=${encodeURIComponent(returnTo)}`;
  };

  const formatDistanceLabel = (distanceM: number) => {
    if (distanceM < 1000) return `${distanceM} m`;
    const km = distanceM / 1000;
    return Number.isInteger(km) ? `${km} km` : `${km.toFixed(1)} km`;
  };

  useEffect(() => {
    if (!location.search) return;

    const params = new URLSearchParams(location.search);
    const qParam = params.get('q') || '';
    const viewParam = params.get('view');
    const sortParam = params.get('sort') || 'relevance';
    const pageParam = parseInt(params.get('page') || '1', 10);
    const distanceParam = clampDistance(parseInt(params.get('distance') || `${DEFAULT_DISTANCE_M}`, 10));
    const categoriesParam = params.get('categories');
    const singleCategoryParam = params.get('category');
    const minPriceParam = parseInt(params.get('minPrice') || '0', 10);
    const maxPriceParam = parseInt(params.get('maxPrice') || '500', 10);
    const ratingParam = parseInt(params.get('rating') || '0', 10);
    const citiesParam = params.get('cities') || '';
    const postalCodesParam = params.get('postalCodes') || '';
    const useDistanceFilterParam = params.get('useDistance') !== '0';
    const resolvedViewMode =
      useDistanceFilterParam && (viewParam === 'grid' || viewParam === 'list' || viewParam === 'map')
        ? viewParam
        : 'grid';

    const parsedCategories = categoriesParam
      ? categoriesParam.split(',').map((value) => value.trim()).filter(Boolean)
      : singleCategoryParam
        ? [singleCategoryParam]
        : [];

    setQuery(qParam);
    setViewMode(resolvedViewMode);
    setSortBy(sortParam);
    setCurrentPage(Number.isNaN(pageParam) || pageParam < 1 ? 1 : pageParam);

    setTempDistance(distanceParam);
    setTempCities(citiesParam);
    setTempPostalCodes(postalCodesParam);

    setFilters((prev) => ({
      ...prev,
      categories: parsedCategories,
      priceRange: [
        Number.isNaN(minPriceParam) ? 0 : Math.max(0, minPriceParam),
        Number.isNaN(maxPriceParam) ? 500 : Math.max(0, maxPriceParam),
      ],
      rating: Number.isNaN(ratingParam) ? 0 : Math.max(0, ratingParam),
      distance: distanceParam,
      cities: citiesParam,
      postalCodes: postalCodesParam,
      useDistanceFilter: useDistanceFilterParam,
    }));
  }, [location.search]);

  useEffect(() => {
    if (location.search) return;

    const raw = sessionStorage.getItem(BROWSE_STATE_STORAGE_KEY);
    if (!raw) return;

    try {
      const stored = JSON.parse(raw) as StoredBrowseState;

      if (typeof stored.query === 'string') {
        setQuery(stored.query);
      }

      if (stored.viewMode === 'grid' || stored.viewMode === 'list' || stored.viewMode === 'map') {
        setViewMode(stored.viewMode);
      }

      if (typeof stored.sortBy === 'string') {
        setSortBy(stored.sortBy);
      }

      if (typeof stored.currentPage === 'number' && stored.currentPage >= 1) {
        setCurrentPage(Math.floor(stored.currentPage));
      }

      if (typeof stored.tempDistance === 'number') {
        setTempDistance(clampDistance(stored.tempDistance));
      }

      if (typeof stored.tempCities === 'string') {
        setTempCities(stored.tempCities);
      }

      if (typeof stored.tempPostalCodes === 'string') {
        setTempPostalCodes(stored.tempPostalCodes);
      }

      if (stored.filters) {
        setFilters((prev) => ({
          ...prev,
          categories: Array.isArray(stored.filters?.categories) ? stored.filters.categories : prev.categories,
          priceRange:
            Array.isArray(stored.filters?.priceRange) && stored.filters.priceRange.length === 2
              ? stored.filters.priceRange
              : prev.priceRange,
          availability: Array.isArray(stored.filters?.availability) ? stored.filters.availability : prev.availability,
          rating: typeof stored.filters?.rating === 'number' ? stored.filters.rating : prev.rating,
          distance:
            typeof stored.filters?.distance === 'number'
              ? clampDistance(stored.filters.distance)
              : prev.distance,
          cities: typeof stored.filters?.cities === 'string' ? stored.filters.cities : prev.cities,
          postalCodes:
            typeof stored.filters?.postalCodes === 'string' ? stored.filters.postalCodes : prev.postalCodes,
          useDistanceFilter:
            typeof stored.filters?.useDistanceFilter === 'boolean'
              ? stored.filters.useDistanceFilter
              : prev.useDistanceFilter,
        }));

        if (stored.filters.useDistanceFilter === false) {
          setViewMode('grid');
        }
      }
    } catch (error) {
      console.error('Failed to restore browse state:', error);
    }
  }, [location.search]);

  useEffect(() => {
    const stateToStore: StoredBrowseState = {
      query,
      viewMode,
      sortBy,
      currentPage,
      tempDistance,
      tempCities,
      tempPostalCodes,
      filters,
    };

    sessionStorage.setItem(BROWSE_STATE_STORAGE_KEY, JSON.stringify(stateToStore));
  }, [query, viewMode, sortBy, currentPage, tempDistance, tempCities, tempPostalCodes, filters]);

  useEffect(() => {
    const params = new URLSearchParams();
    const effectiveViewMode = filters.useDistanceFilter ? viewMode : 'grid';

    const normalizedQuery = query.trim();
    if (normalizedQuery) params.set('q', normalizedQuery);
    if (effectiveViewMode !== 'grid') params.set('view', effectiveViewMode);
    if (sortBy !== 'relevance') params.set('sort', sortBy);
    if (currentPage > 1) params.set('page', String(currentPage));

    if (filters.categories.length > 0) {
      params.set('categories', filters.categories.join(','));
    }

    if (filters.priceRange[0] !== 0) params.set('minPrice', String(filters.priceRange[0]));
    if (filters.priceRange[1] !== 500) params.set('maxPrice', String(filters.priceRange[1]));
    if (filters.rating > 0) params.set('rating', String(filters.rating));
    if (filters.distance !== DEFAULT_DISTANCE_M) params.set('distance', String(filters.distance));
    if (filters.cities.trim()) params.set('cities', filters.cities.trim());
    if (filters.postalCodes.trim()) params.set('postalCodes', filters.postalCodes.trim());
    if (!filters.useDistanceFilter) params.set('useDistance', '0');

    const nextSearch = params.toString();
    const currentSearch = location.search.startsWith('?') ? location.search.slice(1) : location.search;

    if (nextSearch !== currentSearch) {
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : '',
        },
        { replace: true }
      );
    }
  }, [query, viewMode, sortBy, currentPage, filters, location.pathname, location.search, navigate]);

  useEffect(() => {
    const allTools: Tool[] = browseData?.tools?.items || [];
    if (!allTools.length) {
      setTools([]);
      setFilteredTools([]);
      setTotalCount(0);
      return;
    }

    let filtered = [...allTools];

    // Disabled tools must not appear in search/browse results.
    filtered = filtered.filter((tool) => !tool.disabled);

    const normalizedQuery = query.trim().toLowerCase();
    if (normalizedQuery) {
      filtered = filtered.filter((tool) =>
        tool.name?.toLowerCase().includes(normalizedQuery) ||
        tool.description?.toLowerCase().includes(normalizedQuery)
      );
    }

    if (filters.categories.length > 0) {
      filtered = filtered.filter((tool) =>
        tool.categories?.some((category) => filters.categories.includes(category.name))
      );
    }

    filtered = filtered.filter((tool) => {
      const price = (tool.pricePerDayCents || 0) / 100;
      return price >= filters.priceRange[0] && price <= filters.priceRange[1];
    });

    if (filters.rating > 0) {
      filtered = filtered.filter((tool) => {
        const rating = tool.ratingAverage ?? tool.rating ?? 0;
        return rating >= filters.rating;
      });
    }

    const cityFilters = filters.cities
      .split(',')
      .map((city) => city.trim().toLowerCase())
      .filter(Boolean);

    if (cityFilters.length > 0) {
      filtered = filtered.filter((tool) => {
        const city = tool.city?.toLowerCase() || '';
        return cityFilters.some((cityFilter) => city.includes(cityFilter));
      });
    }

    const postalCodeFilters = filters.postalCodes
      .split(',')
      .map((postalCode) => postalCode.trim().toLowerCase())
      .filter(Boolean);

    if (postalCodeFilters.length > 0) {
      filtered = filtered.filter((tool) => {
        const postalCode = tool.postalCode?.toLowerCase() || '';
        return postalCodeFilters.some((postalCodeFilter) => postalCode.includes(postalCodeFilter));
      });
    }

    if (filters.useDistanceFilter && userLocation) {
      filtered = filtered.filter((tool) => {
        const coords = getToolCoordinates(tool);
        if (!coords) return true;
        const distance = calculateDistanceKm(
          userLocation.latitude,
          userLocation.longitude,
          coords.latitude,
          coords.longitude
        );
        return distance <= filters.distance / 1000;
      });
    }

    filtered.sort((a, b) => {
      if (sortBy === 'price_asc') return (a.pricePerDayCents || 0) - (b.pricePerDayCents || 0);
      if (sortBy === 'price_desc') return (b.pricePerDayCents || 0) - (a.pricePerDayCents || 0);
      if (sortBy === 'rating') return (b.ratingAverage || 0) - (a.ratingAverage || 0);
      if (sortBy === 'distance' && userLocation) {
        const aCoords = getToolCoordinates(a);
        const bCoords = getToolCoordinates(b);
        if (!aCoords && !bCoords) return 0;
        if (!aCoords) return 1;
        if (!bCoords) return -1;

        const distanceA = calculateDistanceKm(userLocation.latitude, userLocation.longitude, aCoords.latitude, aCoords.longitude);
        const distanceB = calculateDistanceKm(userLocation.latitude, userLocation.longitude, bCoords.latitude, bCoords.longitude);
        return distanceA - distanceB;
      }
      return 0;
    });

    setTotalCount(filtered.length);
    setFilteredTools(filtered);

    const startIndex = (currentPage - 1) * itemsPerPage;
    const pagedTools = filtered.slice(startIndex, startIndex + itemsPerPage);
    setTools(pagedTools);
  }, [browseData, query, filters, sortBy, currentPage, userLocation]);

  const handleCategoryChange = (category: string) => {
    setCurrentPage(1);
    setFilters(prev => {
      const newCategories = prev.categories.includes(category)
        ? prev.categories.filter(cat => cat !== category)
        : [...prev.categories, category];
      
      return {
        ...prev,
        categories: newCategories
      };
    });
  };
  
  const handlePriceChange = (range: [number, number]) => {
    setCurrentPage(1);
    setFilters(prev => ({
      ...prev,
      priceRange: range
    }));
  };
  
  const handleRatingChange = (rating: number) => {
    setCurrentPage(1);
    setFilters(prev => ({
      ...prev,
      rating: prev.rating === rating ? 0 : rating
    }));
  };
  
  const handleDistanceChange = (distance: number) => {
    const nextDistance = clampDistance(distance);
    setTempDistance(nextDistance);
    setCurrentPage(1);
    setFilters(prev => ({
      ...prev,
      distance: nextDistance
    }));
  };

  const handleDistanceInputChange = (value: string) => {
    const parsed = parseInt(value, 10);
    if (Number.isNaN(parsed)) return;
    handleDistanceChange(parsed);
  };

  const handleCitiesBlur = () => {
    setCurrentPage(1);
    setFilters(prev => ({
      ...prev,
      cities: tempCities
    }));
  };

  const handlePostalCodesBlur = () => {
    setCurrentPage(1);
    setFilters(prev => ({
      ...prev,
      postalCodes: tempPostalCodes
    }));
  };
  
  const clearFilters = () => {
    setFilters({
      categories: [],
      priceRange: [0, 500],
      availability: [],
      rating: 0,
      distance: DEFAULT_DISTANCE_M,
      cities: '',
      postalCodes: '',
      useDistanceFilter: true,
    });
    setTempDistance(DEFAULT_DISTANCE_M);
    setTempCities('');
    setTempPostalCodes('');
    setCurrentPage(1);
    setQuery('');
  };
  
  const toggleFilters = () => {
    setFiltersOpen(!filtersOpen);
  };

  const requestLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          setLocationError(null);
        },
        (error) => {
          console.error('Geolocation error:', error);
          setLocationError('Kunne ikke hente din position');
        }
      );
    }
  };
  
  return (
    <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row gap-8">
        {/* Filters Sidebar */}
        <div className={`md:block ${filtersOpen ? 'block' : 'hidden'} md:w-64 lg:w-72`}>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 sticky top-24">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <Filter className="h-5 w-5 mr-2" />
                Filtre
              </h2>
              <button
                onClick={clearFilters}
                className="text-sm text-green-600 hover:text-green-700"
              >
                Ryd alt
              </button>
            </div>
            
            {/* Categories */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                Kategorier
              </h3>
              <div className="space-y-2">
                {categories.map((category) => (
                  <div key={category.id} className="flex items-center">
                    <input
                      type="checkbox"
                      id={category.id.toString()}
                      checked={filters.categories.includes(category.name)}
                      onChange={() => handleCategoryChange(category.name)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <label htmlFor={category.id.toString()} className="ml-2 text-sm text-gray-700">
                      {category.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Price Range */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                Prisniveau (pr. dag)
              </h3>
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <label htmlFor="min-price" className="sr-only">Min Price</label>
                  <input
                    type="number"
                    id="min-price"
                    min="0"
                    max={filters.priceRange[1]}
                    value={filters.priceRange[0]}
                    onChange={(e) => handlePriceChange([parseInt(e.target.value), filters.priceRange[1]])}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  />
                </div>
                <span className="text-gray-500">-</span>
                <div className="flex-1">
                  <label htmlFor="max-price" className="sr-only">Max Price</label>
                  <input
                    type="number"
                    id="max-price"
                    min={filters.priceRange[0]}
                    value={filters.priceRange[1]}
                    onChange={(e) => handlePriceChange([filters.priceRange[0], parseInt(e.target.value)])}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  />
                </div>
                <span className="text-gray-500">kr.</span>
              </div>
            </div>
            
            {/* Distance */}
            <div className="mb-6">
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-900 mb-2">Byer</h3>
                <input
                  type="text"
                  placeholder="F.eks. Aarhus, Odense"
                  value={tempCities}
                  onChange={(e) => setTempCities(e.target.value)}
                  onBlur={handleCitiesBlur}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.currentTarget.blur();
                    }
                  }}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
                />
              </div>

              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-900 mb-2">Postnumre</h3>
                <input
                  type="text"
                  placeholder="F.eks. 8000, 5000"
                  value={tempPostalCodes}
                  onChange={(e) => setTempPostalCodes(e.target.value)}
                  onBlur={handlePostalCodesBlur}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.currentTarget.blur();
                    }
                  }}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
                />
              </div>

              <div className="mb-3 flex items-center justify-between">
                <label htmlFor="use-distance-filter" className="text-sm font-medium text-gray-900">
                  Brug afstandsmåler
                </label>
                <input
                  id="use-distance-filter"
                  type="checkbox"
                  checked={filters.useDistanceFilter}
                  onChange={(e) => {
                    const useDistanceFilter = e.target.checked;

                    if (!useDistanceFilter && viewMode === 'map') {
                      setViewMode('grid');
                    }

                    setCurrentPage(1);
                    setFilters(prev => ({
                      ...prev,
                      useDistanceFilter
                    }));
                  }}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
              </div>
              
              {/* Location status */}
              <div className="mb-3 text-xs">
                {userLocation ? (
                  <div className="flex items-center text-green-600">
                    <MapPin className="h-3 w-3 mr-1" />
                    <span>Position hentet</span>
                  </div>
                ) : locationError ? (
                  <div className="flex flex-col">
                    <span className="text-red-600 mb-1">{locationError}</span>
                    <button
                      onClick={requestLocation}
                      className="text-green-600 hover:text-green-700 underline text-left"
                    >
                      Prøv igen
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col">
                    <span className="text-gray-500 mb-1">Henter position...</span>
                    <button
                      onClick={requestLocation}
                      className="text-green-600 hover:text-green-700 underline text-left"
                    >
                      Tillad position manuelt
                    </button>
                  </div>
                )}
              </div>
              
              <input
                type="range"
                min={MIN_DISTANCE_M}
                max={MAX_DISTANCE_M}
                step="10"
                value={tempDistance}
                onChange={(e) => handleDistanceChange(parseInt(e.target.value))}
                disabled={!filters.useDistanceFilter}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600 disabled:cursor-not-allowed disabled:opacity-50"
              />
              <div className="mt-3 flex items-center gap-2">
                <label htmlFor="distance-input" className="text-xs text-gray-600">
                  Afstand
                </label>
                <input
                  id="distance-input"
                  type="number"
                  min={MIN_DISTANCE_M}
                  max={MAX_DISTANCE_M}
                  step="10"
                  value={tempDistance}
                  onChange={(e) => handleDistanceInputChange(e.target.value)}
                  disabled={!filters.useDistanceFilter}
                  className="w-28 border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm disabled:cursor-not-allowed disabled:opacity-50"
                />
                <span className="text-xs text-gray-500">meter</span>
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>20m</span>
                <span>2.5km</span>
                <span>5km</span>
              </div>
            </div>
            
            {/* Rating */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                Bedømmelse
              </h3>
              <div className="flex space-x-1">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    onClick={() => handleRatingChange(rating)}
                    className={`h-8 w-8 rounded-md flex items-center justify-center 
                              ${filters.rating >= rating ? 'bg-yellow-100 text-yellow-600' : 'bg-gray-100 text-gray-400'}`}
                  >
                    <span>★</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Apply Filters Button - Mobile Only */}
            <div className="block md:hidden">
              <Button variant="primary" fullWidth onClick={toggleFilters}>
                Avend filtre
              </Button>
            </div>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="flex-1">
          {/* Search and Filter Controls */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              {/* Search Bar */}
              <div className="relative flex-grow">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Søg efter værktøj..."
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm"
                />
              </div>
              
              {/* Filter Toggle - Mobile Only */}
              <div className="flex sm:hidden">
                <Button 
                  variant="outline" 
                  icon={<Filter className="h-4 w-4 mr-2" />}
                  onClick={toggleFilters}
                  fullWidth
                >
                  Filters
                </Button>
              </div>
              
              {/* View Toggle */}
              <div className="hidden sm:flex items-center space-x-2 border border-gray-300 rounded-md">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`flex items-center justify-center h-9 w-9 rounded-l-md ${
                    viewMode === 'grid' ? 'bg-green-50 text-green-600' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <Grid className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex items-center justify-center h-9 w-9 ${
                    viewMode === 'list' ? 'bg-green-50 text-green-600' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <List className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setViewMode('map')}
                  disabled={!filters.useDistanceFilter}
                  className={`flex items-center justify-center h-9 w-9 rounded-r-md ${
                    viewMode === 'map'
                      ? 'bg-green-50 text-green-600'
                      : 'text-gray-500 hover:bg-gray-50'
                  } disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-gray-100`}
                >
                  <Map className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Results Count and Sort */}
          <div className="flex justify-between items-center mb-6">
            <div className="text-sm text-gray-600">
              Viser <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, totalCount)}</span> af <span className="font-medium">{totalCount}</span> værktøj
            </div>
            <div className="flex items-center">
              <span className="text-sm text-gray-700 mr-2">Sortér efter:</span>
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="appearance-none bg-white border border-gray-300 rounded-md py-1.5 pl-3 pr-8 text-sm font-medium text-gray-700 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="relevance">Relevans</option>
                  <option value="price_asc">Pris: Lav til høj</option>
                  <option value="price_desc">Pris: Høj til lav</option>
                  <option value="rating">Bedømmelse</option>
                  <option value="distance">Afstand</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                  <ChevronDown className="h-4 w-4" />
                </div>
              </div>
            </div>
          </div>
          
          {/* No Results */}
          {tools.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Intet værktøj fundet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Prøv at justere din søgning eller filtrene for at finde det, du leder efter.
              </p>
              <div className="mt-6">
                <Button onClick={clearFilters} variant="outline">
                  Nulstil filtre
                </Button>
              </div>
            </div>
          )}
          
          {/* Tool List or Map View */}
          {tools.length > 0 && (
            <>
              {viewMode === 'map' ? (
                <MapView
                  tools={filteredTools}
                  userLocation={userLocation}
                  radiusKm={tempDistance / 1000}
                  buildToolLink={buildToolDetailLink}
                />
              ) : (
                <div className={`
                  ${viewMode === 'grid' 
                    ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6' 
                    : 'space-y-4'
                  }
                `}>
                  {tools.map((tool) => (
                    <ToolCard 
                      key={tool.id} 
                      tool={tool} 
                      variant={viewMode}
                      toolLink={buildToolDetailLink(tool.id)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
          
          {/* Pagination */}
          {totalCount > itemsPerPage && viewMode !== 'map' && (
            <div className="mt-8 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Forrige
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalCount / itemsPerPage), prev + 1))}
                  disabled={currentPage >= Math.ceil(totalCount / itemsPerPage)}
                  className="relative ml-3 inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Næste
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Viser <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> til{' '}
                    <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalCount)}</span> af{' '}
                    <span className="font-medium">{totalCount}</span> resultater
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Forrige</span>
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {Array.from({ length: Math.ceil(totalCount / itemsPerPage) }, (_, i) => i + 1)
                      .filter(page => {
                        const totalPages = Math.ceil(totalCount / itemsPerPage);
                        if (totalPages <= 7) return true;
                        if (page === 1 || page === totalPages) return true;
                        if (page >= currentPage - 1 && page <= currentPage + 1) return true;
                        if (page === currentPage - 2 || page === currentPage + 2) return page;
                        return false;
                      })
                      .map((page, index, array) => {
                        const prevPage = array[index - 1];
                        const showEllipsis = prevPage && page - prevPage > 1;
                        
                        return (
                          <React.Fragment key={page}>
                            {showEllipsis && (
                              <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300">
                                ...
                              </span>
                            )}
                            <button
                              onClick={() => setCurrentPage(page)}
                              className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                currentPage === page
                                  ? 'z-10 bg-green-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600'
                                  : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-offset-0'
                              }`}
                            >
                              {page}
                            </button>
                          </React.Fragment>
                        );
                      })}
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalCount / itemsPerPage), prev + 1))}
                      disabled={currentPage >= Math.ceil(totalCount / itemsPerPage)}
                      className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Næste</span>
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrowseTools;